/**
 * @file src/ui/react/studio/OfflineDownloadControl.tsx
 * @description Download this collection for offline, and see what is already downloaded.
 *
 * It lives in the collection drawer, next to the other things that are about THIS collection, and
 * not on the main bar — a download is something you do to a collection, so it belongs where the
 * collection is. The downloads LIST lives here too rather than on root: the bytes are origin-scoped
 * to this app, so a list on root would be describing storage it cannot see.
 *
 * Everything visible is a library component (`Button`, `Progress`, `Badge`, `Alert`, `Popover`).
 * The app ships the design library's pre-compiled CSS and has no Tailwind pass of its own, so
 * anything bespoke here is an inline `style` — the same rule the rest of this drawer follows.
 *
 * Nothing here interrupts playing. A download runs behind the music, and a refusal or a failure is
 * an inline message, never a modal: the one thing this must not do is stop a listen to talk about
 * storage.
 */
import { useState } from 'react';
import { CloudDownload, HardDriveDownload, RotateCw, Trash2, X } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Progress,
} from 'plantasia.space-design/react';
import { getT } from '../../../i18n/index.js';
import type {
  OfflineCollectionStatus,
  OfflineDownloadProgress,
  OfflineQuality,
  OfflineStorageStatus,
  UseOfflineCollectionResult,
} from './useOfflineCollection';

/** Bytes as a person reads them. Whole GB below a tenth would round two different sizes to "0 GB". */
export function formatBytes(bytes: number | null | undefined): string {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '0 MB';
  const mb = value / (1024 * 1024);
  if (mb < 1) return '<1 MB';
  if (mb < 1024) return `${Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/**
 * How far along a running download is, 0–100.
 *
 * The resolve phase gets the first quarter of the bar. It is a real part of the wait — a per-item
 * hydration measured in seconds — and leaving the bar at zero through it reads as a hang. It is not
 * given half, because the byte download is the longer half and a bar that leaps to the middle and
 * then crawls is its own kind of lie.
 */
function percentOf(progress: OfflineDownloadProgress | null): number {
  if (!progress) return 0;
  if (progress.phase === 'resolving') {
    const total = Number(progress.resolvedTotal) || 0;
    if (!total) return 0;
    return Math.round(((Number(progress.resolved) || 0) / total) * 25);
  }
  // The icon precache sits between resolving and the bytes. It is short and has no count worth
  // showing, so it holds the bar where resolving left it rather than resetting it to zero.
  if (progress.phase === 'interface') return 25;
  const total = Number(progress.total) || 0;
  if (!total) return 25;
  return Math.min(100, 25 + Math.round(((Number(progress.completed) || 0) / total) * 75));
}

/**
 * Two figures, because there are two. The allowance is per person — your own downloads against
 * your own 5 GB — while the disk they sit on is one browser's, shared with everyone else who uses
 * it and with everything else this app stores. Showing only the first would make a browser that is
 * genuinely full unexplainable; blending them would make the allowance unreadable.
 */
function StorageLine({ storage, count, max }: { storage: OfflineStorageStatus | null; count: number; max: number }) {
  const t = getT();
  if (!storage) return null;
  const percent = storage.maxBytes ? Math.min(100, (storage.usedBytes / storage.maxBytes) * 100) : 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem' }}>
        <span>{t('studio.offline.usage', {
          used: formatBytes(storage.usedBytes),
          allowed: formatBytes(storage.maxBytes),
        })}</span>
        <span style={{ opacity: 0.7 }}>{t('studio.offline.collectionCount', { count, max })}</span>
      </div>
      <Progress value={percent} />
      {storage.originUsage === null ? null : (
        <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>
          {t('studio.offline.browserUsage', { used: formatBytes(storage.originUsage) })}
        </span>
      )}
    </div>
  );
}

/**
 * Which copy to keep, asked once, before anything is fetched.
 *
 * Two buttons rather than a setting to change afterwards: the whole point is that nobody downloads
 * the same collection twice. A choice made here costs one download; a choice made after the fact
 * costs two, which is the thing this exists to prevent.
 *
 * Lossless is the normalized master the platform already streams at high quality — not the uploaded
 * original, which no download can reach. A track that has no lossless copy quietly keeps its
 * compressed one; that is a fact about that track, not a state worth making anybody read.
 */
function QualityDialog({
  open,
  onCancel,
  onChoose,
}: {
  open: boolean;
  onCancel: () => void;
  onChoose: (quality: OfflineQuality) => void;
}) {
  const t = getT();
  return (
    <AlertDialog open={open} onOpenChange={(next: boolean) => { if (!next) onCancel(); }}>
      {/* No `aria-describedby={undefined}`: Radix spreads it over the id it generated for the
          description, and a screen reader would then hear the title and none of the explanation. */}
      <AlertDialogContent className="dark orb-studio__portal-surface">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('studio.offline.qualityTitle', 'Download for offline')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('studio.offline.qualityQuestion', 'Which copy would you like to keep?')}
            {' '}
            {/* Said here because here is where the choice is made. Lossless exists only for tracks
                uploaded that way; asking for it does not invent one, it keeps the compressed copy
                for the tracks that have no other. */}
            {t('studio.offline.qualityNote', 'Lossless only where the track has it.')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {/* Stacked, not a row. Both labels name a format, and two of those side by side are wider
            than the dialog — they pushed past its edge rather than wrapping. Down the panel they
            fit at any width and read as what they are: two answers to one question. */}
        <AlertDialogFooter style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          {/* Plain buttons, NOT `AlertDialogAction`: the library hardcodes
              `buttonVariants({ variant: 'destructive' })` onto that component, and it survives even
              `asChild` — Radix merges the className onto the child, so a normal Button inside one
              still comes out red. Nothing here is being destroyed. The dialog's open state is ours,
              so closing it is a `setState`, which is all the wrapper was providing.

              The formats are named because "compressed" and "lossless" are not facts anybody can
              check and these are: the platform encodes its MP3 at 320 kbps and normalizes its
              master to 16-bit 44.1 kHz. */}
          <Button onClick={() => onChoose('compressed')}>
            {t('studio.offline.qualityCompressedSpec', 'Compressed · MP3 320 kbps')}
          </Button>
          <Button variant="secondary" onClick={() => onChoose('lossless')}>
            {t('studio.offline.qualityLosslessSpec', 'Lossless · WAV 16-bit 44.1 kHz')}
          </Button>
          <Button variant="ghost" onClick={onCancel}>{t('studio.offline.cancel')}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function LibraryRow({
  entry,
  onRemove,
  busy,
}: {
  entry: OfflineCollectionStatus;
  onRemove: (collectionId: string) => void;
  busy: boolean;
}) {
  const t = getT();
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        // Room on the right for the button's hover corners, which are drawn OUTSIDE its box. The
        // list clips horizontally (a long name would otherwise widen the popover), so a button
        // flush against that edge has its corner marks sliced off by the clip.
        padding: '6px 6px 6px 0',
        borderTop: '1px solid color-mix(in oklab, var(--color-foreground, #ffffff) 12%, transparent)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.name || t('studio.offline.untitledCollection')}
        </div>
        <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
          {t('studio.offline.rowDetail', {
            tracks: entry.entryCount,
            size: formatBytes(entry.bytes),
          })}
          {' · '}
          {entry.quality === 'lossless'
            ? t('studio.offline.qualityLossless', 'Lossless')
            : t('studio.offline.qualityCompressed', 'Compressed')}
        </div>
      </div>
      {/* Both of these keep their size; the row gives way on the NAME, which is the only part
          that can be shortened without losing meaning (it already ellipsises). Letting them shrink
          instead is what pushed the row wide enough to overflow in the first place. */}
      <span style={{ flexShrink: 0 }}>
        {entry.state === 'ready' ? (
          <Badge tone="positive" variant="secondary">{t('studio.offline.ready')}</Badge>
        ) : (
          <Badge tone="warning" variant="secondary">{t('studio.offline.partial')}</Badge>
        )}
      </span>
      <Button
        variant="ghost"
        size="icon"
        disabled={busy}
        onClick={() => onRemove(entry.collectionId)}
        aria-label={t('studio.offline.remove')}
        style={{ width: 28, height: 28, flexShrink: 0 }}
      >
        <Trash2 style={{ width: 15, height: 15 }} />
      </Button>
    </li>
  );
}

export interface OfflineDownloadControlProps {
  offline: UseOfflineCollectionResult;
  /** False outside collection mode (a queue, an inline roster) — there is no collection to download. */
  canDownload: boolean;
}

/**
 * The drawer's offline control: one button that reflects the collection's state, and a popover
 * holding the used-of-allowed readout and every downloaded collection.
 *
 * The state the button shows is COMPUTED from what is actually stored, not from what a download
 * last reported — a collection whose bytes the browser reclaimed reads as partial and offers to
 * finish, which is the only answer that does not lie.
 */
export function OfflineDownloadControl({ offline, canDownload }: OfflineDownloadControlProps) {
  const t = getT();
  const { status, progress, library, storage, maxCollections, capError, error, busy } = offline;
  // The question is asked per download and nothing remembers the answer: a remembered choice is a
  // global quality setting by the back door.
  const [askingQuality, setAskingQuality] = useState(false);

  if (!offline.enabled) return null;

  // `busy` covers the gap before there is anything stored to report on: the resolve phase writes no
  // manifest, so a state read from the store alone would show "not downloaded" while a download was
  // plainly underway.
  const running = Boolean(status?.running) || (busy && progress?.phase !== 'done');
  // Every audio file is gone — the browser reclaimed the storage. Checked BEFORE `partial`, because
  // it is a different sentence: "finish downloading" implies something is here to finish, and there
  // is not. `persist()` is a request rather than a promise, so this is a state to name, not hide.
  const evicted = Boolean(status?.evicted) && !running;
  const ready = status?.state === 'ready' && !running && !evicted;
  const partial = status?.state === 'partial' && !running && !evicted;
  const percent = percentOf(progress);

  const primaryLabel = running
    ? t('studio.offline.downloading')
    : ready
    ? t('studio.offline.downloaded')
    : partial
    ? t('studio.offline.finish')
    : t('studio.offline.download');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={primaryLabel}
          style={{
            width: 28,
            height: 28,
            borderRadius: 9999,
            background: ready || running
              ? 'color-mix(in oklab, var(--color-foreground, #ffffff) 18%, transparent)'
              : 'transparent',
            color: 'var(--color-foreground, #ffffff)',
            opacity: ready || running ? 1 : 0.35,
          }}
        >
          {running ? (
            <HardDriveDownload style={{ width: 16, height: 16 }} />
          ) : (
            <CloudDownload style={{ width: 16, height: 16 }} />
          )}
        </Button>
      </PopoverTrigger>
      {/* `dark` + the studio's portal surface, exactly as the card context menu does: a Radix portal
          escapes the drawer's theme scope, so the surface has to carry it itself. */}
      <PopoverContent
        align="end"
        className="dark orb-studio__portal-surface"
        style={{ width: 320, display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <div style={{ fontWeight: 600 }}>{t('studio.offline.title')}</div>

        {canDownload ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {running ? (
              <>
                <Progress value={percent} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem' }}>
                  <span style={{ flex: 1 }}>
                    {!progress || progress.phase === 'resolving'
                      ? t('studio.offline.resolving')
                      : progress.phase === 'measuring'
                      // Between resolving and fetching: each object is asked how big it is, so the
                      // download can be refused before it starts rather than half way through.
                      ? t('studio.offline.measuring', 'Checking sizes…')
                      : progress.phase === 'interface'
                      ? t('studio.offline.interface')
                      : t('studio.offline.progress', {
                          done: progress.completed ?? 0,
                          total: progress.total ?? 0,
                        })}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => void offline.cancel()}>
                    <X style={{ width: 14, height: 14 }} />
                    <span>{t('studio.offline.cancel')}</span>
                  </Button>
                </div>
              </>
            ) : evicted ? (
              // Honest about what happened, and offering the only thing that fixes it. Never a
              // silent "ready" over bytes that are not there.
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Badge variant="destructive">{t('studio.offline.evicted')}</Badge>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => void offline.resume()}
                    style={{ marginLeft: 'auto' }}
                  >
                    <RotateCw style={{ width: 14, height: 14 }} />
                    <span>{t('studio.offline.redownload')}</span>
                  </Button>
                </div>
                <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7 }}>
                  {t('studio.offline.evictedDetail')}
                </p>
              </div>
            ) : ready ? null : partial ? (
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => void offline.resume()}>
                <RotateCw style={{ width: 14, height: 14 }} />
                <span>{t('studio.offline.finish')}</span>
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setAskingQuality(true)}
              >
                <CloudDownload style={{ width: 14, height: 14 }} />
                <span>{t('studio.offline.download')}</span>
              </Button>
            )}

            {/* Said plainly rather than hidden behind a help icon: bytes in a browser profile are
                not a guarantee, and someone about to rely on them offline should know that before
                they travel, not after. */}
            <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.6 }}>{t('studio.offline.caveat')}</p>
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: '0.8125rem', opacity: 0.7 }}>{t('studio.offline.noCollection')}</p>
        )}

        {capError ? (
          <>
          <Alert variant="destructive">
            <AlertDescription>
              {capError.reason === 'collection-limit'
                ? t('studio.offline.capCollections', { max: maxCollections })
                : capError.reason === 'nothing-downloadable'
                ? t('studio.offline.capNothing')
                : capError.reason === 'would-not-fit'
                // Refused before anything was fetched, so the numbers are the whole point: what it
                // needs, what is free, and how much has to go. A refusal that only says "full"
                // leaves someone guessing which collection to remove.
                ? t('studio.offline.capWouldNotFit', {
                    required: formatBytes(Number(capError.detail?.required) || 0),
                    shortBy: formatBytes(Number(capError.detail?.shortBy) || 0),
                  })
                : t('studio.offline.capBytes', { allowed: formatBytes(storage?.maxBytes) })}
            </AlertDescription>
          </Alert>
          {/* What removing each of the others would actually give back — the number that answers
              "which one do I remove", and not the same as a collection's own size, because bytes two
              collections share come back from neither on its own. */}
          {capError.reason === 'would-not-fit' && library.length ? (
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.75rem', opacity: 0.8 }}>
              {library
                // Never the collection being downloaded: on a re-download it is in this list, and
                // suggesting somebody remove the thing they just asked for is not advice.
                .filter((entry) => entry.collectionId !== status?.collectionId)
                .filter((entry) => (entry.removalSavings ?? 0) > 0)
                .map((entry) => (
                  <li key={entry.collectionId}>
                    {t('studio.offline.wouldFree', {
                      name: entry.name || t('studio.offline.untitledCollection'),
                      size: formatBytes(entry.removalSavings),
                      defaultValue: '{{name}} — frees {{size}}',
                    })}
                  </li>
                ))}
            </ul>
          ) : null}
          </>
        ) : null}

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {/* Some entries can be un-downloadable while the rest are fine — a draft, or a track whose
            release carries no compressed variant. Named, because a collection that quietly comes
            back one track short offline is a worse surprise than one that says so now. */}
        {status?.skippedTrackIds?.length ? (
          <Alert>
            <AlertDescription>
              {t('studio.offline.skipped', { count: status.skippedTrackIds.length })}
            </AlertDescription>
          </Alert>
        ) : null}

        <StorageLine storage={storage} count={library.length} max={maxCollections} />

        {library.length ? (
          // `overflowX: hidden` is load-bearing, not tidiness. Setting only `overflowY: auto` makes
          // the OTHER axis compute to `auto` too, so a row a few pixels too wide — the vertical
          // scrollbar alone takes enough — raised a horizontal scrollbar. That scrollbar is itself
          // several pixels tall, which changed the row's height, which moved the button out from
          // under the pointer, which dropped the hover, which shrank it back: the list visibly
          // oscillated for as long as the trash icon was hovered.
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              maxHeight: 200,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {/* ONE description of a downloaded collection, in one layout. The collection on screen
                used to be summarised above as well, in a different shape, saying the same four
                things twice. It is a row here like every other, and the block above is only ever
                about DOING something to it. */}
            {library.map((entry) => (
              <LibraryRow
                key={entry.collectionId}
                entry={entry}
                busy={busy}
                onRemove={(id) => void offline.remove(id)}
              />
            ))}
          </ul>
        ) : null}
      </PopoverContent>
      <QualityDialog
        open={askingQuality}
        onCancel={() => setAskingQuality(false)}
        onChoose={(quality) => {
          setAskingQuality(false);
          void offline.download(quality);
        }}
      />
    </Popover>
  );
}
